import { useEffect, useState } from "react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { Header } from "@/components/Header";
import { getLibrary, deleteLibrary, getKeys, exportLibraryEntry } from "@/lib/api";
import { Footer } from "@/components/Footer";
import { Library as LibIcon, Trash2, FileText, Clock, Beaker, FileDown, Braces, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

const fmt = (iso) => {
  try {
    return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + "Z";
  } catch {
    return iso;
  }
};

const Field = ({ label, value }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-950/40 p-3">
    <div className="flex items-center gap-1.5 mb-1">
      <FileText className="h-3 w-3 text-cyan-400" />
      <span className="mono text-[10px] uppercase tracking-widest text-cyan-400">{label}</span>
    </div>
    <p className="text-xs text-slate-300 leading-relaxed">{value}</p>
  </div>
);

export default function LibraryPage() {
  const [items, setItems] = useState([]);
  const [pubkeys, setPubkeys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [domainF, setDomainF] = useState("all");
  const [faultF, setFaultF] = useState("all");

  useEffect(() => {
    getLibrary()
      .then((d) => setItems(d))
      .catch(() => {})
      .finally(() => setLoading(false));
    getKeys().then(setPubkeys).catch(() => {});
  }, []);

  const remove = async (id) => {
    try {
      await deleteLibrary(id);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Analysis deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const seqLabel = (it) => String(it.batch_seq || 0).padStart(3, "0");

  const exportJson = async (it) => {
    try {
      const data = await exportLibraryEntry(it.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analysis_${seqLabel(it)}_signed.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Signed JSON exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const exportPdf = (it) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const H = doc.internal.pageSize.getHeight();
    const margin = 48;
    // Header band
    doc.setFillColor(6, 10, 18);
    doc.rect(0, 0, W, 84, "F");
    doc.setFont("courier", "bold");
    doc.setFontSize(18);
    doc.setTextColor(34, 211, 238);
    doc.text("BioGovernor — DBCA Live", margin, 44);
    doc.setFont("courier", "normal");
    doc.setFontSize(9);
    doc.setTextColor(148, 163, 184);
    doc.text("Fault Event Analysis  ·  Audit Ledger Export", margin, 64);

    let y = 116;
    const meta = [
      ["Domain", it.domain],
      ["Fault Type", it.fault_type],
      ["Event Timestamp", fmt(it.event_timestamp)],
      ["Ledger Entry", `#${seqLabel(it)}`],
      ["Merkle Root", it.merkle_root || "—"],
    ];
    doc.setFontSize(9);
    meta.forEach(([k, v]) => {
      doc.setFont("helvetica", "bold");
      doc.setTextColor(8, 145, 178);
      doc.text(k.toUpperCase(), margin, y);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 41, 59);
      const lines = doc.splitTextToSize(String(v), W - margin - 150);
      doc.text(lines, margin + 120, y);
      y += Math.max(16, lines.length * 12 + 4);
    });

    y += 8;
    doc.setDrawColor(226, 232, 240);
    doc.line(margin, y, W - margin, y);
    y += 22;

    const fields = [
      ["Fault Type", it.breakdown.fault_type],
      ["Governor Action", it.breakdown.governor_action],
      ["Correction Applied", it.breakdown.correction_applied],
      ["Outcome", it.breakdown.outcome],
    ];
    fields.forEach(([label, value]) => {
      if (y > H - 90) {
        doc.addPage();
        y = margin;
      }
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(8, 145, 178);
      doc.text(label.toUpperCase(), margin, y);
      y += 16;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(30, 41, 59);
      const lines = doc.splitTextToSize(value, W - margin * 2);
      doc.text(lines, margin, y);
      y += lines.length * 13 + 18;
    });

    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(
      `Generated ${new Date().toISOString().slice(0, 19)}Z  ·  Read-only analysis export  ·  Ed25519 governed`,
      margin,
      H - 40
    );
    doc.save(`analysis_${seqLabel(it)}.pdf`);
    toast.success("PDF exported");
  };

  const domains = [...new Set(items.map((i) => i.domain))];
  const faults = [...new Set(items.map((i) => i.fault_type))];
  const filtered = items.filter((it) => {
    if (domainF !== "all" && it.domain !== domainF) return false;
    if (faultF !== "all" && it.fault_type !== faultF) return false;
    if (q.trim()) {
      const hay = (
        it.domain + " " + it.fault_type + " " + Object.values(it.breakdown || {}).join(" ")
      ).toLowerCase();
      if (!hay.includes(q.trim().toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen control-grid-bg">
      <Header quarantine={false} pubkeys={pubkeys} />
      <div className="max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        <div>
          <h2 className="mono text-lg font-bold text-slate-100 flex items-center gap-2">
            <LibIcon className="h-5 w-5 text-cyan-400" /> Analysis Library
          </h2>
          <p className="mono text-[11px] text-slate-500 mt-1">
            Saved fault-event breakdowns · read-only · {filtered.length}
            {filtered.length !== items.length ? ` of ${items.length}` : ""} entries
          </p>
        </div>

        {items.length > 0 && (
          <div className="flex flex-col md:flex-row gap-3" data-testid="library-filters">
            <div className="relative flex-1">
              <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="library-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search notes, domains, fault types…"
                className="pl-9 bg-slate-950/60 border-slate-800 text-slate-200 mono text-xs focus-visible:ring-cyan-500/50"
              />
            </div>
            <Select value={domainF} onValueChange={setDomainF}>
              <SelectTrigger data-testid="library-domain-filter" className="md:w-64 bg-slate-950/60 border-slate-800 text-slate-200 mono text-xs">
                <SelectValue placeholder="All domains" />
              </SelectTrigger>
              <SelectContent className="bg-[#0b1120] border-slate-800 text-slate-200">
                <SelectItem value="all">All domains</SelectItem>
                {domains.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={faultF} onValueChange={setFaultF}>
              <SelectTrigger data-testid="library-fault-filter" className="md:w-64 bg-slate-950/60 border-slate-800 text-slate-200 mono text-xs">
                <SelectValue placeholder="All fault types" />
              </SelectTrigger>
              <SelectContent className="bg-[#0b1120] border-slate-800 text-slate-200">
                <SelectItem value="all">All fault types</SelectItem>
                {faults.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {loading ? (
          <p className="mono text-xs text-slate-500">Loading…</p>
        ) : items.length === 0 ? (
          <div className="glass-panel rounded-2xl p-12 text-center" data-testid="library-empty">
            <LibIcon className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-300 font-medium">No saved analyses yet</p>
            <p className="mono text-[11px] text-slate-500 mt-1">
              On the dashboard, approve a correction → click Analyze → Save to Library.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="glass-panel rounded-2xl p-12 text-center" data-testid="library-no-match">
            <Search className="h-10 w-10 text-slate-600 mx-auto mb-3" />
            <p className="text-sm text-slate-300 font-medium">No analyses match your filters</p>
            <p className="mono text-[11px] text-slate-500 mt-1">Try clearing the search or changing the filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5" data-testid="library-grid">
            {filtered.map((it) => (
              <div key={it.id} data-testid="library-entry" className="glass-panel rounded-2xl p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <span className="inline-flex items-center gap-1.5 mono text-[10px] text-cyan-300 mb-1">
                      <Beaker className="h-3 w-3" /> {it.domain}
                    </span>
                    <h3 className="mono text-sm font-bold text-amber-300">{it.fault_type}</h3>
                    <p className="mono text-[10px] text-slate-500 flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" /> {fmt(it.event_timestamp)} · entry #
                      {String(it.batch_seq || 0).padStart(3, "0")}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      data-testid="export-pdf-button"
                      variant="ghost"
                      size="sm"
                      onClick={() => exportPdf(it)}
                      className="text-slate-400 hover:text-cyan-300 hover:bg-cyan-950/30 mono text-[10px]"
                      title="Download as PDF"
                    >
                      <FileDown className="h-4 w-4 mr-1" /> PDF
                    </Button>
                    <Button
                      data-testid="export-json-button"
                      variant="ghost"
                      size="sm"
                      onClick={() => exportJson(it)}
                      className="text-slate-400 hover:text-teal-300 hover:bg-teal-950/30 mono text-[10px]"
                      title="Download signed JSON"
                    >
                      <Braces className="h-4 w-4 mr-1" /> JSON
                    </Button>
                    <Button
                      data-testid="library-delete-button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(it.id)}
                      className="text-slate-500 hover:text-red-400 hover:bg-red-950/30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2.5">
                  <Field label="Fault Type" value={it.breakdown.fault_type} />
                  <Field label="Governor Action" value={it.breakdown.governor_action} />
                  <Field label="Correction Applied" value={it.breakdown.correction_applied} />
                  <Field label="Outcome" value={it.breakdown.outcome} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
